import {
	Select,
	TagsInput,
	TextInput,
} from '@components/v2/ui';
import { DateInput } from '@components/v2/ui/DateInput/DateInput';
import { NumberInput } from '@components/v2/ui/NumberInput';
import i18n from '@config/i18n/config';
import { TOffsetTag } from '@global-types/index';
import {
	ActionIcon,
	Box,
	Group,
	SimpleGrid,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import useStore from '@store/index';
import { IconPlus } from '@tabler/icons-react';
import { convertArrayToLabelValue } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBrands } from 'shared/api/get-brands';
import { useOffsetTags } from 'shared/api/get-offset-tags';
import DrawerCreateBrand from '../../DrawerCreateBrand2/DrawerCreateBrand2';
import DrawerCreateContact from '../../DrawerCreateContact/DrawerCreateContact';
import DrawerCreateOrganization from '../../DrawerCreateOrganization/DrawerCreateOrganization';
import DrawerCreateTag from '../../DrawerCreateTag/DrawerCreateTag';
import classes from '../CreateOffset.module.css';

const Step1 = ({ form }: { form: any }) => {
	/* ******************************** Store States ******************************* */
	const {
		openDrawerOrganization,
		setOpenDrawerOrganization,
		openDrawerContact,
		setOpenDrawerContact,
		createdBrand,
		setCreatedBrand,
	} = useStore();

	const { t } = useTranslation();
	useEffect(() => {
		if (createdBrand !== '') {
			form.setFieldValue('brand', createdBrand);
			setCreatedBrand('');
		}
	}, [createdBrand]);

	const { data: brands } = useBrands();
	const { data: apiOffsetTags } = useOffsetTags();

	const [opened, { open, close }] = useDisclosure(false);

	const [
		openedBrand,
		{ open: openBrand, close: closeBrand },
	] = useDisclosure(false);

	const [newlyCreatedTags, setNewlyCreatedTags] =
		useState<Partial<TOffsetTag>[]>([]);

	const allAvailableTags = useMemo(() => {
		const apiTags = Array.isArray(apiOffsetTags)
			? apiOffsetTags
			: [];
		return [...apiTags, ...newlyCreatedTags];
	}, [apiOffsetTags, newlyCreatedTags]);

	const uniqueTagsData = useMemo(() => {
		const seenLabels = new Set();
		const result: {
			value: string;
			label: string;
			color: string;
		}[] = [];

		allAvailableTags.forEach((tag: any) => {
			const currentLabel =
				tag.label ||
				(i18n.language === 'en'
					? tag.value?.en
					: tag.value?.fr);

			if (currentLabel) {
				const normalizedLabel = currentLabel
					.trim()
					.toLowerCase();
				if (!seenLabels.has(normalizedLabel)) {
					seenLabels.add(normalizedLabel);
					result.push({
						value: tag.id.toString(),
						label: currentLabel,
						color: tag.color,
					});
				}
			}
		});

		return result;
	}, [allAvailableTags, i18n.language]);

	return (
		<Box
			mt={'48px'}
			className={classes['container-form']}
		>
			<DrawerCreateContact
				opened={openDrawerContact}
				onClose={() => {
					setOpenDrawerContact(false);
				}}
			/>
			<DrawerCreateOrganization
				opened={openDrawerOrganization}
				onClose={() => {
					setOpenDrawerOrganization(false);
				}}
			/>
			<DrawerCreateBrand
				opened={openedBrand}
				onClose={closeBrand}
			/>
			<DrawerCreateTag
				opened={opened}
				onClose={close}
				onSuccess={(tag: TOffsetTag) => {
					const newLabel =
						i18n.language === 'en'
							? tag.value?.en
							: tag.value?.fr;

					setNewlyCreatedTags((prev) => [
						...prev,
						{
							id: tag.id,
							color: tag.color,
							label: newLabel,
						},
					]);

					const currentTags = form.getValues().tags || [];
					form.setFieldValue('tags', [
						...currentTags,
						tag.id.toString(),
					]);
				}}
			/>

			<SimpleGrid
				spacing="xs"
				verticalSpacing="xs"
				cols={{ base: 1, sm: 2 }}
			>
				<TextInput
					withAsterisk
					size="md"
					label={t('offsets.form.name')}
					autoFocus
					placeholder={t('global.name')}
					{...form.getInputProps('name')}
				/>

				<NumberInput
					max={15000}
					min={0}
					decimalScale={
						Math.trunc(
							form.getInputProps('quantity').value || 0
						) === 0
							? 1
							: 2
					}
					visibleFrom="xxl"
					size="md"
					label={t('global.quantity')}
					withAsterisk
					unit={getUnitLabel('tco2e')}
					defaultValue={0}
					{...form.getInputProps('quantity')}
				/>
				<Group
					gap={'12px'}
					align="flex-start"
					wrap="nowrap"
				>
					<Box style={{ flex: 1 }}>
						<Select
							searchable
							size="md"
							label={t('global.brand')}
							withAsterisk
							data={convertArrayToLabelValue(brands)}
							placeholder={t('placeholders.selectBrand')}
							key={form.key('brand')}
							{...form.getInputProps('brand')}
						/>
					</Box>
					<ActionIcon
						variant="outline"
						size="48px"
						radius={'100%'}
						color={'#d7d7ddff'}
						onClick={openBrand}
						style={{ marginTop: '25px' }}
					>
						<IconPlus
							style={{ width: '60%', height: '60%' }}
							stroke={1.5}
							color="#1D3965"
						/>
					</ActionIcon>
				</Group>

				<Group
					gap={'12px'}
					align="flex-start"
					wrap="nowrap"
				>
					<Box style={{ flex: 1 }}>
						<TagsInput
							radius={'xl'}
							placeholder={
								form.values.tags?.length > 0 ? '' : 'Tags'
							}
							size={'md'}
							label={t('global.tags')}
							withAsterisk
							data={uniqueTagsData}
							{...form.getInputProps('tags')}
						/>
					</Box>
					<ActionIcon
						variant="outline"
						size="48px"
						radius={'100%'}
						color={'#d7d7ddff'}
						onClick={open}
						style={{ marginTop: '25px' }}
					>
						<IconPlus
							style={{ width: '60%', height: '60%' }}
							stroke={1.5}
							color="#1D3965"
						/>
					</ActionIcon>
				</Group>

				<DateInput
					clearable
					defaultDate={new Date()}
					label={t('offsets.form.emissionStartDate')}
					withAsterisk
					placeholder="DD/MM/YYYY"
					minDate={dayjs('1970-01-01').toDate()}
					{...form.getInputProps('startDate')}
				/>

				<DateInput
					clearable
					label={t('offsets.form.emissionEndDate')}
					withAsterisk
					placeholder="DD/MM/YYYY"
					minDate={
						form.getInputProps('startDate').value
							? dayjs(
									form.getInputProps('startDate').value
								).toDate()
							: dayjs('1970-01-01').toDate()
					}
					{...form.getInputProps('endDate')}
				/>
			</SimpleGrid>
		</Box>
	);
};

export default Step1;
